import { reauthenticateForDeletion } from '../../components/account/reauthenticate-for-deletion';
import * as client from '../../libs/webauthn/client';
import { isBase64url, parseBase64url } from '../../libs/webauthn/utils';
import { getCurrentUser } from './auth';
import { webauthnServerUrl } from './config';

const baseUrl = webauthnServerUrl;

const isRunningLocally =
  typeof window !== 'undefined' && window.location.hostname === 'localhost';

const BYPASS_MFA_ENABLED =
  isRunningLocally ||
  process?.env?.REACT_APP_TODESKTOP_BYPASS_MFA === 'true' ||
  process?.env?.TODESKTOP_BYPASS_MFA === 'true';

const BYPASS_SECURITY_TOKEN = {
  aaguid: '00000000-0000-0000-0000-000000000000',
  counter: 0,
  icon_dark: '',
  icon_light: '',
  name: 'Bypass security token',
};

export function shouldBypassWebauthn() {
  return BYPASS_MFA_ENABLED;
}

function getBypassSecurityToken() {
  return { ...BYPASS_SECURITY_TOKEN };
}

export async function register() {
  if (shouldBypassWebauthn()) {
    return getBypassSecurityToken();
  }
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('No authenticated user');
  const idToken = await currentUser.getIdToken();
  const username = currentUser.email;

  const challengeResponse = await fetch(`${baseUrl}/register/challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ username }),
  });

  if (!challengeResponse.ok) {
    const json = await challengeResponse.json();
    if (json.error || json.message) {
      throw new Error(json.error || json.message);
    }
    throw new Error('Failed to get registration challenge');
  }

  const challengeText = await challengeResponse.json();

  const registration = await client.register({
    challenge: challengeText,
    user: username ?? '',
    userVerification: 'preferred',
  });

  const verifyResponse = await fetch(`${baseUrl}/register/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      registration,
      username,
    }),
  });

  if (!verifyResponse.ok) {
    const error = await verifyResponse.text();
    throw new Error(`Registration failed: ${error}`);
  }
  return verifyResponse.json();
}

// Destructive actions opt into strict errors; existing optional-details callers
// keep their fallback for failed HTTP requests.
export async function getSecurityTokenDetails({ throwOnError = false } = {}) {
  if (shouldBypassWebauthn()) {
    return getBypassSecurityToken();
  }
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('No authenticated user');
  const idToken = await currentUser.getIdToken();
  try {
    const response = await fetch(`${baseUrl}/getSecurityTokenDetails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ username: currentUser.email }),
    });
    if (response.status === 404) {
      if (!throwOnError) return null;
      const result = await response.json();
      if (result?.message === 'No security token registered') return null;
      throw new Error('Unable to check your security token. Please try again.');
    }
    if (!response.ok) {
      if (!throwOnError) return null;
      throw new Error('Unable to check your security token. Please try again.');
    }
    const details = await response.json();
    if (
      throwOnError &&
      (!details || typeof details !== 'object' || Array.isArray(details))
    ) {
      throw new Error('Unable to check your security token. Please try again.');
    }
    return details;
  } catch (error) {
    if (throwOnError) throw error;
    console.error(error);
    return null;
  }
}

/** The server repeats these checks before deleting any account data. */
export async function authenticateAccountDeletion() {
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('No authenticated user');
  const { uid, email } = currentUser;
  const assertSameUser = () => {
    const user = getCurrentUser();
    if (user?.uid !== uid || user?.email !== email) {
      throw new Error('Your account changed. Refresh the page and try again.');
    }
  };

  let hasSecurityToken: boolean;
  try {
    hasSecurityToken = Boolean(
      await getSecurityTokenDetails({ throwOnError: true }),
    );
  } catch {
    throw new Error('Unable to check your security token. Please try again.');
  }
  assertSameUser();
  if (hasSecurityToken) {
    const authentication = await authenticate();
    assertSameUser();
    return authentication;
  }

  await reauthenticateForDeletion(currentUser);
  assertSameUser();
  return undefined;
}

export async function authenticate() {
  if (shouldBypassWebauthn()) {
    return { bypass: true };
  }
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('No authenticated user');
  const idToken = await currentUser.getIdToken();
  const username = currentUser.email;

  const challengeResponse = await fetch(`${baseUrl}/login/challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ username }),
  });

  if (!challengeResponse.ok) {
    const json = await challengeResponse.json();
    if (json.error || json.message) {
      throw new Error(json.error || json.message);
    }
    throw new Error('Failed to get registration challenge');
  }

  let challenge: string;
  let credentialIds: string[];
  try {
    ({ challenge, credentialIds } = await challengeResponse.json());
    if (
      typeof challenge !== 'string' ||
      !challenge ||
      !Array.isArray(credentialIds) ||
      credentialIds.length === 0 ||
      !credentialIds.every(
        (id) =>
          typeof id === 'string' &&
          isBase64url(id) &&
          parseBase64url(id).byteLength > 0,
      )
    ) {
      throw new Error('Invalid passkey challenge response');
    }
  } catch {
    throw new Error(
      'Unable to load your registered passkeys. Refresh the page and try again.',
    );
  }

  const authentication = await client.authenticate({
    challenge,
    allowCredentials: credentialIds,
    timeout: 60000,
    userVerification: 'preferred',
  });
  return authentication;
}

export async function verifyAuthentication(authentication: unknown) {
  if (shouldBypassWebauthn()) {
    return { success: true };
  }
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('No authenticated user');
  const idToken = await currentUser.getIdToken();
  const username = currentUser.email;

  const verifyLoginResponse = await fetch(`${baseUrl}/login/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      authentication,
      username,
    }),
  });
  return verifyLoginResponse.json();
}

export class NoSecurityTokenError extends Error {
  constructor() {
    super('No security token registered');
  }
}

export class ReleaseBypassPermissionError extends Error {
  constructor() {
    super('You do not have permission to manage release bypass for this app.');
  }
}

async function getResponseMessage(response: Response, fallback: string) {
  try {
    const json = await response.json();
    return json.message || json.error || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Enables or disables release bypass for an app. Requires a WebAuthn
 * security-token tap; the server binds the challenge to the (appId, enabled)
 * pair, so both steps send the same values.
 */
export async function setReleaseBypass(
  appId: string,
  enabled: boolean,
): Promise<boolean> {
  if (shouldBypassWebauthn()) {
    return enabled;
  }
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('No authenticated user');
  const username = currentUser.email;

  async function requestChallenge(forceRefreshToken = false): Promise<{
    challenge: string;
    credentialIds: string[];
  }> {
    const idToken = await currentUser!.getIdToken(forceRefreshToken);
    const response = await fetch(`${baseUrl}/release-bypass/challenge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ username, appId, enabled }),
    });
    if (response.ok) {
      return response.json();
    }
    if (response.status === 401 && !forceRefreshToken) {
      return requestChallenge(true);
    }
    if (response.status === 404) {
      throw new NoSecurityTokenError();
    }
    throw new Error(
      await getResponseMessage(
        response,
        'Failed to get release bypass challenge',
      ),
    );
  }

  // Returns the new state, or 'restart' if the challenge expired while the
  // user was at the key-tap prompt (the ceremony must start over).
  async function verify(authentication: unknown): Promise<boolean | 'restart'> {
    let forceRefreshToken = false;
    for (let attempt = 0; attempt < 4; attempt++) {
      const isLastAttempt = attempt === 3;
      const idToken = await currentUser!.getIdToken(forceRefreshToken);
      forceRefreshToken = false;
      const response = await fetch(`${baseUrl}/release-bypass/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ username, appId, enabled, authentication }),
      });
      if (response.ok) {
        return (await response.json()).allowed === true;
      }
      const responseMessage = await getResponseMessage(
        response,
        'Release bypass verification failed',
      );
      // The challenge is preserved server-side for these two cases, so the
      // same verify body can be re-sent without a new key tap.
      if (response.status === 401 && !isLastAttempt) {
        forceRefreshToken = true;
        continue;
      }
      if (response.status === 502 && !isLastAttempt) {
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * 2 ** attempt),
        );
        continue;
      }
      if (
        response.status === 400 &&
        responseMessage === 'Release bypass challenge expired'
      ) {
        return 'restart';
      }
      if (response.status === 403) {
        throw new ReleaseBypassPermissionError();
      }
      if (response.status === 404) {
        throw new NoSecurityTokenError();
      }
      throw new Error(responseMessage);
    }
    throw new Error('Release bypass verification failed');
  }

  // Allow one silent restart when the challenge expires at the key-tap prompt.
  for (let ceremony = 0; ceremony < 2; ceremony++) {
    const { challenge, credentialIds } = await requestChallenge();
    const authentication = await client.authenticate({
      challenge,
      allowCredentials: credentialIds,
      timeout: 60000,
      userVerification: 'preferred',
    });
    const result = await verify(authentication);
    if (result !== 'restart') {
      return result;
    }
  }
  throw new Error('Release bypass challenge expired');
}

export async function appIsAllowedToReleaseWithoutWebauthn(appId: string) {
  if (shouldBypassWebauthn()) {
    return true;
  }
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('No authenticated user');
  const username = currentUser.email;

  const appAllowedToReleaseWithoutWebauthn = await fetch(
    `${baseUrl}/app-allowed-to-release-without-webauthn`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, appId }),
    },
  );
  return (
    appAllowedToReleaseWithoutWebauthn.ok &&
    (await appAllowedToReleaseWithoutWebauthn.json()).allowed === true
  );
}
